/**
 * R232q5s Tier 3: FlightDialog — heraldic destination picker for AIWARI
 * flightmasters. Opens via window event 'aiwari-server-flight-open' with
 * detail.npcId. Sends `flight-list-dest`, listens `flight-list-dest-result`.
 *
 * On click → `flight-fly`. On `flight-arrived` shows toast. UI matches
 * ServerNpcDialog/Auction stylistic: parchment-on-dark with corner ornaments,
 * Cinzel headlines, EB Garamond body, gold accents.
 */

import { useEffect, useState } from 'react';
import { getRoom } from '../game/realmClient.js';

interface FlightDest {
  toNodeId: string;
  toZoneId: string;
  toName: string;
  costGold: number;
  travelMs: number;
}

interface FlightFromNode {
  id: string;
  zoneId: string;
  name: string;
  faction: string;
}

interface ListResultOk {
  ok: true;
  from: FlightFromNode;
  dests: FlightDest[];
}

interface ListResultErr {
  ok: false;
  reason: string;
}

const ZONE_LABELS_RU: Record<string, string> = {
  silver_fields: 'Серебряные Поля',
  blood_mountain: 'Кровогорье',
  ragged_sails_bay: 'Залив Рваных Парусов',
  lyov_glade: 'Лощина Светлых Ив',
  dusk_thicket: 'Сумеречные Чащи',
  coldridge: 'Хладногорье',
  ash_steppes: 'Ясеневые Степи',
  misty_cliffs: 'Туманные Утёсы',
  salt_mines: 'Соляные Копи',
  swamp_chambers: 'Болотные Чертоги',
  split_altar_ridge: 'Хребет Расколотого Алтаря',
  torn_veil: 'Прорванная Завеса',
};

function formatTravelTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}м ${s}с` : `${s}с`;
}

export function FlightDialog() {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState<FlightFromNode | null>(null);
  const [dests, setDests] = useState<FlightDest[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flying, setFlying] = useState<{ to: FlightDest; startedAt: number } | null>(null);
  const [, setTick] = useState(0);

  // Open via window event from NpcInteractPrompt/Nameplate when npc.npcType === 'flightmaster'.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ npcId: string }>).detail;
      if (!detail?.npcId) return;
      setOpen(true);
      setFrom(null);
      setDests([]);
      setError(null);
      setFlying(null);
      setBusy(true);
      const room = getRoom();
      if (!room) { setBusy(false); setError('Связь со Стормвейлом потеряна'); return; }
      room.send('flight-list-dest', { npcId: detail.npcId });
    };
    window.addEventListener('aiwari-server-flight-open', handler as EventListener);
    return () => window.removeEventListener('aiwari-server-flight-open', handler as EventListener);
  }, []);

  // Server message subscriptions.
  useEffect(() => {
    const room = getRoom();
    if (!room) return;
    room.onMessage('flight-list-dest-result', (msg: ListResultOk | ListResultErr) => {
      setBusy(false);
      if (!msg.ok) {
        setError(reasonRu(msg.reason));
        return;
      }
      setError(null);
      setFrom(msg.from);
      setDests(msg.dests ?? []);
    });
    room.onMessage('flight-fly-result', (msg: { ok: boolean; reason?: string; toNodeId?: string; costGold?: number; travelMs?: number }) => {
      setBusy(false);
      if (!msg.ok) {
        setError(flyReasonRu(msg.reason));
        setFlying(null);
        return;
      }
      const dest = dests.find((d) => d.toNodeId === msg.toNodeId);
      if (dest) setFlying({ to: dest, startedAt: Date.now() });
    });
    room.onMessage('flight-arrived', () => {
      setFlying(null);
      setOpen(false);
    });
  }, [dests]);

  // Tick animation while flying.
  useEffect(() => {
    if (!flying) return;
    const id = window.setInterval(() => setTick((t) => (t + 1) & 0xfff), 200);
    return () => window.clearInterval(id);
  }, [flying]);

  // Esc closes (unless mid-flight).
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !flying) setOpen(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, flying]);

  if (!open) return null;

  const fly = (dest: FlightDest) => {
    if (!from) return;
    const room = getRoom();
    if (!room) return;
    setBusy(true);
    setError(null);
    room.send('flight-fly', { fromNodeId: from.id, toNodeId: dest.toNodeId });
  };

  // Mid-flight UI overrides.
  if (flying) {
    const elapsed = Date.now() - flying.startedAt;
    const total = flying.to.travelMs;
    const pct = Math.min(100, Math.floor((elapsed / total) * 100));
    return (
      <div
        className="fixed inset-0 z-[90] flex items-center justify-center pointer-events-auto"
        style={{ background: 'radial-gradient(circle at center, rgba(11,9,7,0.85) 0%, rgba(0,0,0,0.97) 90%)', backdropFilter: 'blur(6px)' }}
      >
        <div
          className="text-center px-12 py-10"
          style={{
            background: 'linear-gradient(180deg, #161210 0%, #0B0907 100%)',
            border: '2px solid #5C4520',
            boxShadow: '0 0 60px rgba(201,169,106,0.4)',
            fontFamily: 'EB Garamond, Georgia, serif',
            color: '#E8DCC0',
            minWidth: 420,
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.4em] mb-2" style={{ color: '#9C8E72', fontFamily: 'Spectral SC, serif' }}>
            Грифон в небе
          </div>
          <h2 className="text-[28px] mb-4" style={{ fontFamily: 'Cinzel, serif', color: '#C9A96A', fontWeight: 700 }}>
            ✈ {flying.to.toName}
          </h2>
          <div className="text-[14px] italic mb-6" style={{ color: '#9C8E72' }}>
            Ветер несёт тебя к {ZONE_LABELS_RU[flying.to.toZoneId] ?? flying.to.toZoneId}…
          </div>
          <div style={{ height: 4, background: '#3A2818', position: 'relative', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #C9A96A, #E8C77B)', transition: 'width 200ms linear' }} />
          </div>
          <div className="text-[12px] mt-3" style={{ color: '#E8C77B', fontFamily: 'JetBrains Mono, monospace' }}>
            {formatTravelTime(Math.max(0, total - elapsed))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-6 pointer-events-auto"
      style={{ background: 'radial-gradient(circle at center, rgba(11,9,7,0.78) 0%, rgba(0,0,0,0.92) 90%)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div
        className="relative max-w-[640px] w-[92vw] max-h-[88vh] flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #161210 0%, #0B0907 100%)',
          border: '2px solid #5C4520',
          boxShadow: '0 0 60px rgba(201,169,106,0.3), inset 0 0 40px rgba(58,40,24,0.4)',
          fontFamily: 'EB Garamond, Georgia, serif',
          color: '#E8DCC0',
        }}
      >
        <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />

        {/* Header */}
        <div className="flex items-baseline justify-between px-6 pt-6 pb-3 border-b" style={{ borderColor: '#3A2818' }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: '#9C8E72', fontFamily: 'Spectral SC, serif' }}>
              Хранитель Грифонов
            </div>
            <h2 className="text-[24px]" style={{ fontFamily: 'Cinzel, serif', color: '#C9A96A', fontWeight: 700 }}>
              {from?.name ?? 'Куда летим, путник?'}
            </h2>
            {from && (
              <div className="text-[12px] italic mt-1" style={{ color: '#9C8E72' }}>
                Откуда: {ZONE_LABELS_RU[from.zoneId] ?? from.zoneId}
              </div>
            )}
          </div>
          <button onClick={() => setOpen(false)} className="text-[24px] hover:scale-110 transition" style={{ color: '#9C8E72' }}>×</button>
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 pt-3">
            <div className="px-3 py-2 text-[12px] italic"
              style={{ background: 'rgba(163,50,50,0.16)', color: '#A33232', border: '1px solid #A33232' }}>
              {error}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {busy && dests.length === 0 ? (
            <div className="text-[13px] italic text-center py-8" style={{ color: '#9C8E72' }}>
              Свитки разворачиваются…
            </div>
          ) : dests.length === 0 ? (
            <div className="text-[13px] italic text-center py-8" style={{ color: '#9C8E72' }}>
              {error ? 'Свитков нет.' : 'Связи с другими ветрами нет — этот узел изолирован.'}
            </div>
          ) : (
            <div className="space-y-1.5">
              {dests.map((d) => (
                <DestRow key={d.toNodeId} dest={d} onFly={() => fly(d)} disabled={busy} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-2 text-[10px] uppercase tracking-[0.3em] text-center" style={{ background: 'rgba(58,40,24,0.4)', borderTop: '1px solid #3A2818', fontFamily: 'Spectral SC, serif', color: '#9C8E72' }}>
          Esc · Уйти · сервер списывает {dests.length > 0 ? 'ауреи' : ''}
        </div>
      </div>
    </div>
  );
}

function DestRow({ dest, onFly, disabled }: { dest: FlightDest; onFly: () => void; disabled: boolean }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2"
      style={{ background: 'rgba(58,40,24,0.3)', border: '1px solid #3A2818' }}
    >
      <div className="flex-1 min-w-0">
        <div className="truncate text-[14px]" style={{ fontFamily: 'Cinzel, serif', fontWeight: 600, color: '#C9A96A' }}>
          ✈ {dest.toName}
        </div>
        <div className="text-[11px] italic" style={{ color: '#9C8E72' }}>
          {ZONE_LABELS_RU[dest.toZoneId] ?? dest.toZoneId}
        </div>
      </div>
      <div className="text-right text-[11px]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        <div style={{ color: '#E8C77B' }}>{dest.costGold} ауреев</div>
        <div style={{ color: '#9C8E72' }}>{formatTravelTime(dest.travelMs)}</div>
      </div>
      <button
        type="button"
        onClick={onFly}
        disabled={disabled}
        className="text-[10px] px-3 py-1.5 hover:scale-[1.05] transition"
        style={{
          background: 'linear-gradient(180deg, #C9A96A, #8A6B2E)',
          color: '#0B0907',
          border: '1px solid #3A2818',
          fontFamily: 'Cinzel, serif',
          fontWeight: 600,
          letterSpacing: '0.1em',
          cursor: disabled ? 'wait' : 'pointer',
          textTransform: 'uppercase',
        }}
      >
        Лететь
      </button>
    </div>
  );
}

function reasonRu(reason?: string): string {
  switch (reason) {
    case 'unknown-node': return 'Этот хранитель ещё не открыл маршруты.';
    default: return reason ?? 'Неизвестная ошибка';
  }
}

function flyReasonRu(reason?: string): string {
  switch (reason) {
    case 'unknown-from': return 'Не вижу твоего узла.';
    case 'unknown-to': return 'Этой точки нет на картах.';
    case 'same': return 'Ты уже здесь.';
    case 'insufficient-gold': return 'Не хватает ауреев на полёт.';
    case 'cross-faction-locked': return 'В этот край грифон не полетит — чужая земля.';
    default: return reason ?? 'Полёт не удался';
  }
}

function Corner({ pos }: { pos: 'tl' | 'tr' | 'bl' | 'br' }) {
  const cls: Record<typeof pos, string> = {
    tl: 'top-2 left-2',
    tr: 'top-2 right-2 scale-x-[-1]',
    bl: 'bottom-2 left-2 scale-y-[-1]',
    br: 'bottom-2 right-2 scale-x-[-1] scale-y-[-1]',
  };
  return (
    <svg
      className={`absolute ${cls[pos]} w-7 h-7 pointer-events-none`}
      viewBox="0 0 28 28"
      fill="none"
      stroke="#5C4520"
      strokeWidth="1.5"
    >
      <path d="M2 2 L10 2 M2 2 L2 10" />
      <path d="M3 3 Q9 3 9 9" />
      <circle cx="5" cy="5" r="1" fill="#5C4520" />
    </svg>
  );
}
